import _ from 'lodash';
import React, { useMemo } from 'react';
import { Field, Form } from 'formik';

// Material UI
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

// Data
import { ICreatePlayer, ICreatePlayerContactFields } from './create-player.interface';
import { getCreatePlayerMessages } from './create-player.messages';
import { GENDER_OPTIONS } from '../../../lib/constants';

// Components
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { FormikInput } from '../../_core/Formik/formik-input.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { FormHelperText } from '../../_core/_ui/forms.component';
import { OutlineButton, PrimaryButton } from '../../_core/_ui/buttons.component';
import { FormikDatePicker } from '../../_core/Formik/formik-datepicker.component';
import { FormikSelect } from '../../_core/Formik/formik-select.component';
import { SectionHeading } from '../../_core/_ui/typography.component';
import { SectionWrapper } from '../../_core/_ui/structure.components';
import { PlayerContactFields } from '../../PlayerContact/player-contact-fields.component';
import { ContactSummaryRow } from '../../PlayerContact/contact-summary-row.component';
import { PrimarySportFormOptions, Sport, SportTerminology } from '../../../lib/types';
import { useCompanySport, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';

const FORM_ID = 'create-player-form';

export const CreatePlayer: React.FC<ICreatePlayer> = ({
  form,
  submitError,
  showSaved,
  ageGroupOptions,
  ageGroupFixed,
  createAgeGroup,
  positionOptions,
  fetchError,
  loading,
}) => {
  const sport = useCompanySport()
  const isDefaultSport = sport === Sport.default
  const termLookup = useCompanySportTerminology()
  const messages = getCreatePlayerMessages(termLookup)

  const { isSubmitting, values, setFieldValue } = form;
  // The last row is the one being typed; the ones before it are already added,
  // so they are what the redundancy check compares the draft against.
  const draftPath = `contacts[${values.contacts.length - 1}]`;
  const addedContacts = useMemo(() => values.contacts.slice(0, -1), [values.contacts]);
  const genderOptions = [..._.filter(GENDER_OPTIONS, option => option.value !== 'Any'), { value: 'Other', label: 'Other' }];

  if (fetchError) return <Box mt={2}><Typography>Unable to create {pluralTerm(termLookup[SportTerminology.ATHLETE].toLowerCase())} at this time. Please try again later</Typography></Box>;

  const removeContact = (index: number) => {
    let nextContacts: ICreatePlayerContactFields[] = [];

    if (index === 0) {
      nextContacts = values.contacts.slice(1);
    } else if (index === values.contacts.length - 1) {
      nextContacts = values.contacts.slice(0, values.contacts.length - 2);
    } else {
      nextContacts = [...values.contacts.slice(0, index), ...values.contacts.slice(index + 1)];
    }

    setFieldValue('contacts', nextContacts);
  };

  return (
    <Form id={FORM_ID}>
      <FocusFirstInvalidField formId={FORM_ID} />
      <Box mt={2}><Typography variant="h5">{termLookup[SportTerminology.ATHLETE]} Information</Typography></Box>
      <Box display="flex">
        <Box mr={1} flexGrow={1}>
          <Field fid="cpf" name="firstName" component={FormikInput} label={messages.lblFirstName} />
        </Box>
        <Box flexGrow={1}>
          <Field fid="cpf" name="lastName" component={FormikInput} label={messages.lblLastName} />
        </Box>
      </Box>
      <Field fid="cpf" name="nickname" component={FormikInput} label={messages.lblNickname} />

      <Box display="flex">
        <Box mr={1} flexGrow={1}>
          <Field fid="csf" name="date" component={FormikDatePicker} label={messages.lblDate} dateFormat="YYYY/MM/DD" />
        </Box>
        <Box flexGrow={1}>
          <Field fid="csf" name="gender" component={FormikSelect} label={messages.lblGender} options={genderOptions} />
        </Box>
      </Box>
      <Field fid="cpf" name="externalId" component={FormikInput} label={messages.lblExternalId} />

      <Field
        fid="cpf"
        name="positionId"
        component={FormikSelect}
        label={messages.lblPosition}
        options={positionOptions}
        isLoading={loading}
      />

      <Field
        fid="cpf"
        name="ageGroupId"
        component={FormikSelect}
        label={messages.lblAgeGroup}
        options={ageGroupOptions}
        isLoading={loading}
        createMethod={createAgeGroup}
        canCreate={!!createAgeGroup}
        disabled={ageGroupFixed}
      />

      {isDefaultSport && 
        <Field
          fid="cpf"
          name="primarySport"
          component={FormikSelect}
          label={messages.lblPrimarySport}
          options={PrimarySportFormOptions}
        />
      }

      <Box my={2}>
        <Typography variant="h5" gutterBottom>Parent / Guardian Information</Typography>
        <SectionWrapper dark padding="8px 24px">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SectionHeading>Contacts</SectionHeading>
              <List>
                {values.contacts.map((contact, index: number) => {
                  if (index === values.contacts.length - 1) return <></>;

                  return (
                    <ContactSummaryRow
                      key={contact._id || index}
                      contact={{ ...contact, relationship: contact.relationship?.value as string }}
                      actions={(
                        <IconButton edge="end" aria-label="delete" onClick={() => removeContact(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    />
                  );
                })}
                {values.contacts.length <= 1 && <ListItem><ListItemText primary="None" /></ListItem>}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <PlayerContactFields sport={sport} namePrefix={draftPath} siblingContacts={addedContacts} />

              <FormControl margin="dense">
                <OutlineButton
                  onClick={() => setFieldValue('contacts', [...values.contacts, { name: '', phone: '', email: '', sendEmail: true, sendSMS: false, height: '', sportInvolvement: null, relationship: null}])}
                >Add Contact</OutlineButton>
              </FormControl>
            </Grid>
          </Grid>
        </SectionWrapper>
      </Box>

      <FormControl margin="dense">
        <Box display="flex" flexDirection="row" alignItems="center">
          <PrimaryButton disabled={isSubmitting} type="submit">
            {isSubmitting && <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />}
            {isSubmitting ? <IntlMsg {...messages.btnSubmitting} /> : <IntlMsg {...messages.btnSubmit} />}
          </PrimaryButton>
          {submitError && <Box ml={2}><FormHelperText error>{submitError}</FormHelperText></Box>}
          {showSaved && <Box ml={2}><FormHelperText className="success">Successfully created</FormHelperText></Box>}
        </Box>
      </FormControl>
    </Form>
  );
};
